import { ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import UICommonCover from 'assets/img/ui_common_cover.svg';
import UIIllustrationPostCoverSkeleton from 'assets/img/ui_illustration_post_cover_skeleton.svg';
import UIPhotoMe from 'assets/img/ui_photo_me.png';
import BaseLayout from 'components/BaseLayout';
import IconSend from 'components/IconSend';
import PostNotFound from 'components/PostNotFound';
import TabbedFolderSection from 'components/TabbedFolderSection';
import TextMeModal from 'components/TextMeModal';
import ToastNotification from 'components/ToastNotification';
import tools, { Tool } from 'data/tools';
import { RootState } from 'store/store';
import {
  fetchLatestPosts,
  postSelector,
  PostState,
} from 'store/features/post/postSlice';

function Home(): ReactElement<HTMLDivElement> {
  const dispatch = useDispatch();
  const post = useSelector<RootState, PostState>(postSelector);
  const [modalVisibility, setModalVisibility] = useState<boolean>(false);

  useEffect(() => {
    if (post.status === 'idle') {
      dispatch(fetchLatestPosts());
    }
  }, [post.status, dispatch]);

  return (
    <BaseLayout>
      <ToastNotification />

      <TextMeModal
        show={modalVisibility}
        onClose={() => {
          setModalVisibility((show) => !show);
        }}
      />

      <section className="mb-28">
        <header
          className="h-56 rounded-xl relative bg-center"
          style={{
            backgroundImage: `url(${UICommonCover})`,
          }}
        >
          <div className="mx-10 -mt-4 flex flex-row hidden absolute top-0 right-0">
            <button className="mr-5 h-8 w-28 bg-indigo-200"></button>
            <button className="mr-5 h-8 w-28 bg-indigo-200"></button>
            <button className="h-8 w-28 bg-indigo-200"></button>
          </div>
          <img
            className="mx-5 -mb-20 h-36 w-36 border-8 border-white rounded-full bg-gray-100 absolute bottom-0 object-cover lg:h-44 lg:w-44"
            width="176"
            height="176"
            src={UIPhotoMe}
            alt="M. Akbar Nugroho"
          />
        </header>
      </section>

      <div className="flex flex-col lg:flex-row">
        <div className="mb-20 lg:mb-0 lg:w-1/4">
          <aside className="py-10 px-5 rounded-xl bg-white">
            <h1 className=" text-secondary font-display">Hi,</h1>
            <p className="mb-2.5 text-sm text-gray-700 font-body">
              I am a software engineer with 2 years of experience, especially in
              making web-based application and REST API.
            </p>
            <p className="mb-2.5 text-sm text-gray-700 font-body">
              My goal is to create what you are planning by working as a team or
              just two of us.
            </p>
            <button
              className="py-2 w-full flex flex-row justify-center items-center bg-primary text-white font-display focus:outline-none hover:bg-secondary"
              onClick={() => setModalVisibility(true)}
            >
              <span className="mr-1">Text Me</span>
              <IconSend width={19} height={19} />
            </button>
          </aside>
        </div>
        <div className="mb-12 lg:ml-12 lg:flex-grow">
          <TabbedFolderSection title="Profile">
            <p className="text-gray-700"></p>
          </TabbedFolderSection>

          <TabbedFolderSection title="Tools">
            <div className="grid grid-cols-3 gap-y-5 place-items-center place-content-center lg:grid-cols-6 lg:gap-y-8">
              {tools.map((tool: Tool) => (
                <div key={tool.name}>
                  <img
                    className="h-16 w-16"
                    src={tool.image}
                    alt={`${tool.name} logo`}
                  />
                </div>
              ))}
            </div>
          </TabbedFolderSection>
        </div>
      </div>

      <section className="mb-24 grid grid-cols-1 gap-y-5 lg:grid-cols-3 lg:gap-10">
        {post.entities.length === 0 ? (
          <div className="col-span-3">
            <PostNotFound />
          </div>
        ) : (
          <article className="h-64 rounded-lg bg-white">
            <div className="h-40 rounded-t-lg">
              <img
                className="rounded-t-xl"
                src={UIIllustrationPostCoverSkeleton}
                alt="post cover skeleton"
              />
            </div>
          </article>
        )}
      </section>
    </BaseLayout>
  );
}

export default Home;
